import { PaymentMethodsEnum } from '@ctct/domain-layer';
import type { BillingAccount, BillingInfo } from '@ctct/domain-layer';
import { applicationStateSessionStorage, envs, urlParams } from '@ctct/infrastructure-layer';
import type { IPaymentMethodsSlice, PaymentMethodsPageStore } from '@ctct/state-client-layer';
import { createPaymentMethodsSliceDefaultValues } from '@ctct/state-client-layer';

import type { TranslationGetter } from '../../../types';
import { paymentMethodsEventBus } from '../PaymentMethodsEventBus';

export class PaymentMethodsService {
  constructor(
    private pageStore: PaymentMethodsPageStore,
    private billingInfo: BillingInfo,
    private billingAccount: BillingAccount,
    private t: TranslationGetter,
    private isApplePayEnabled?: boolean
  ) {}

  public api() {
    return {
      /**
       * if new payment method OR updating the current one
       */
      isNewPaymentMethod: () => {
        const { paymentMethodCanBeUpdated } = this.pageStore.getState();

        return paymentMethodCanBeUpdated;
      },

      setTabsLoading: (val: boolean) => this.pageStore.setState({ tabsLoading: val }),

      setFullLoading: (val: boolean) => this.pageStore.setState({ paymentMethodsFullLoading: val }),

      resetState: () => this.initializeFromGivenState(createPaymentMethodsSliceDefaultValues()),

      setCurrentPaymentMethodWarning: () =>
        this.pageStore.setState({
          currentPaymentMethodWarning: this.t('application-layer.payment.payment_method_not_working_warning'),
        }),

      resetCurrentPaymentMethodWarning: () =>
        this.pageStore.setState({
          currentPaymentMethodWarning: null,
        }),

      beginningExternalRedirectToCompletePayment: () => {
        this.pageStore.setState({
          paymentMethodsFullLoading: true,
          paymentMethodsExternalRedirectLoading: true,
        });
      },
    };
  }

  public data() {
    return {
      getBuyNowPaymentMethods: (userCurrency: string | null) => {
        const INR_CURRENCY = 'INR';

        const unsupportedByPaypal = [INR_CURRENCY];

        const paymentMethods = Object.values(PaymentMethodsEnum);

        return paymentMethods.filter((method) => {
          if (method === PaymentMethodsEnum.ECHECK || method === PaymentMethodsEnum.AMAZON_PAY) {
            return false;
          }

          if (method === PaymentMethodsEnum.APPLE_PAY) {
            return Boolean(this.isApplePayEnabled);
          }

          if (method === PaymentMethodsEnum.PAYPAL && userCurrency) {
            return !unsupportedByPaypal.includes(userCurrency);
          }

          return true;
        });
      },

      getPaymentMethods: () => {
        const CAD_CURRENCY = 'CAD';
        const INR_CURRENCY = 'INR';
        const MXN_CURRENCY = 'MXN';
        const userCurrency = this.billingAccount.getCurrency().toUpperCase();

        const unsupportedByAmazonPay = [CAD_CURRENCY, INR_CURRENCY, MXN_CURRENCY];
        const unsupportedByPaypal = [INR_CURRENCY];

        const paymentMethods = Object.values(PaymentMethodsEnum);

        return paymentMethods.filter((method) => {
          if (method === PaymentMethodsEnum.AMAZON_PAY) {
            // https://ctct.slack.com/archives/C07RSDVFDP0/p1749135473932999?thread_ts=1748960893.222809&cid=C07RSDVFDP0
            const isFeSecureApp = envs.isSecure();

            return !unsupportedByAmazonPay.includes(userCurrency) && !isFeSecureApp;
          }

          if (method === PaymentMethodsEnum.PAYPAL) {
            return !unsupportedByPaypal.includes(userCurrency);
          }

          // ENABLED APPLE PAY AS PAYMENT METHOD
          if (method === PaymentMethodsEnum.APPLE_PAY) {
            return Boolean(this.isApplePayEnabled);
          }

          return true;
        });
      },

      getSelectedMethod: () => this.pageStore.getState().selectedMethod,

      getCurrentPaymentMethod: () => this.getCurrentPaymentMethod(),

      isCreditCard: (method = this.pageStore.getState().selectedMethod) => method === PaymentMethodsEnum.CREDIT_CARD,
      isEcheck: (method = this.pageStore.getState().selectedMethod) => method === PaymentMethodsEnum.ECHECK,
      isPayPal: (method = this.pageStore.getState().selectedMethod) => method === PaymentMethodsEnum.PAYPAL,
      isAmazonPay: (method = this.pageStore.getState().selectedMethod) => method === PaymentMethodsEnum.AMAZON_PAY,
      isApplePay: (method = this.pageStore.getState().selectedMethod) =>
        method === PaymentMethodsEnum.APPLE_PAY && this.isApplePayEnabled,

      // Loading state used in Stripe; we keep a stub here for API compatibility.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      getIsUpdatingStripeLocaleConfig: (_: boolean) => false,
    };
  }

  public handlers() {
    return {
      onPaymentMethodSelect: (selectedMethod: PaymentMethodsEnum, shouldKeepApplePayMobileButton = false) => {
        const prevSelectedMethod = this.pageStore.getState().selectedMethod;

        const prevApplePayAddedCssToIframe = this.pageStore.getState().applePayAddedCssToIframe;

        if (prevSelectedMethod === selectedMethod) return;

        const { currentPaymentMethod, securePayEdited, paypalAgreementId, amazonPayCheckoutSession } =
          this.pageStore.getState();

        const paymentMethodCanBeUpdated = Boolean(
          // new payment method
          selectedMethod !== currentPaymentMethod ||
            // existing secure pay payment method, but new data
            ([PaymentMethodsEnum.ECHECK, PaymentMethodsEnum.CREDIT_CARD].includes(selectedMethod) && securePayEdited) ||
            // existing paypal payment method, but new data
            (selectedMethod === PaymentMethodsEnum.PAYPAL && paypalAgreementId) ||
            // existing amazon pay payment method, but new data
            (selectedMethod === PaymentMethodsEnum.AMAZON_PAY && amazonPayCheckoutSession)
        );

        // reset secure pay state
        this.pageStore.setState({
          addedCssToIframe: false,
          securePayEdited: false,
          showSecurePayIframeBlockedMessage: false,
          selectedMethod,
          paymentMethodCanBeUpdated,
          // mobile apple pay button is always visible (on mobile), we do not want to refresh it on each payment method switch
          applePayAddedCssToIframe: shouldKeepApplePayMobileButton && prevApplePayAddedCssToIframe,
          applePaySecurePayIframeBlockedMessage: false,
        });

        paymentMethodsEventBus.emit('paymentMethodCanBeUpdated', paymentMethodCanBeUpdated, { source: 'user' });
      },
    };
  }

  // --------------
  // Public methods
  // --------------

  public init() {
    this.initializeFromGivenState({
      ...createPaymentMethodsSliceDefaultValues(),
      ...(applicationStateSessionStorage.loadPaymentMethodsState(
        this.billingAccount.getCustomerId()
      ) as Partial<IPaymentMethodsSlice>),
    });
  }

  public isLoading() {
    return this.pageStore.getState().paymentMethodsFullLoading;
  }

  private initializeFromGivenState(initState: IPaymentMethodsSlice) {
    // set payment method based on existing user's method
    const currentPaymentMethod = this.getCurrentPaymentMethod();

    const paymentMethods = this.data().getPaymentMethods();
    const isPaymentMethodAvailable = (method: PaymentMethodsEnum) => paymentMethods.includes(method);

    if (currentPaymentMethod && isPaymentMethodAvailable(currentPaymentMethod)) {
      initState.currentPaymentMethod = currentPaymentMethod;
      initState.selectedMethod = currentPaymentMethod;

      switch (currentPaymentMethod) {
        case PaymentMethodsEnum.PAYPAL:
          initState.paypalConnected = true;
          break;

        case PaymentMethodsEnum.CREDIT_CARD:
          initState.creditCardConnected = true;
          break;

        case PaymentMethodsEnum.ECHECK:
          initState.echeckConnected = true;
          break;

        case PaymentMethodsEnum.AMAZON_PAY:
          initState.amazonPayConnected = true;
          break;

        case PaymentMethodsEnum.APPLE_PAY:
          initState.applePayConnected = true;
          initState.paymentMethodCanBeUpdated = false;
          break;

        default:
          break;
      }
    } else {
      initState.paymentMethodCanBeUpdated = true;
    }

    // set payment method based on tokens in url params
    if (urlParams.getPaypalToken() && isPaymentMethodAvailable(PaymentMethodsEnum.PAYPAL)) {
      initState.selectedMethod = PaymentMethodsEnum.PAYPAL;
      initState.paymentMethodCanBeUpdated = true;
    }

    if (urlParams.getAmazonCheckoutSessionId() && isPaymentMethodAvailable(PaymentMethodsEnum.AMAZON_PAY)) {
      initState.selectedMethod = PaymentMethodsEnum.AMAZON_PAY;
      initState.paymentMethodCanBeUpdated = true;
    }

    this.pageStore.setState(initState);
    paymentMethodsEventBus.emit('paymentMethodCanBeUpdated', initState.paymentMethodCanBeUpdated, {
      source: 'onMount',
    });
  }

  private getCurrentPaymentMethod() {
    if (this.billingInfo.getCreditCardInfo()) {
      if (this.billingInfo.getDigitalWalletInfo() === PaymentMethodsEnum.APPLE_PAY && this.isApplePayEnabled) {
        return PaymentMethodsEnum.APPLE_PAY;
      }

      return PaymentMethodsEnum.CREDIT_CARD;
    }

    if (this.billingInfo.getEcheckInfo()) {
      return PaymentMethodsEnum.ECHECK;
    }

    if (this.billingInfo.getAmazonPayInfo()) {
      return PaymentMethodsEnum.AMAZON_PAY;
    }

    if (this.billingInfo.getPaypalInfo()) {
      return PaymentMethodsEnum.PAYPAL;
    }

    return null;
  }
}
